/**
 * Theme: light, dark or system. The boot script in index.html applies the
 * stored choice before first paint with the same rules; keep the two in step.
 *
 * - The choice lives in localStorage['sao-theme'] (default 'system').
 * - The resolved theme is the class `dark` on <html> (shadcn's convention).
 */
import { useSyncExternalStore } from 'react';
import { readStorage, STORAGE_KEYS, writeStorage } from '@/lib/storage';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEMES: readonly Theme[] = ['light', 'dark', 'system'];

export const THEME_LABELS: Record<Theme, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
};

const DARK_QUERY = '(prefers-color-scheme: dark)';

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark' || value === 'system';
}

function systemTheme(): ResolvedTheme {
  try {
    return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

let theme: Theme = (() => {
  const stored = readStorage(STORAGE_KEYS.theme);
  return isTheme(stored) ? stored : 'system';
})();

const listeners = new Set<() => void>();

function resolve(): ResolvedTheme {
  return theme === 'system' ? systemTheme() : theme;
}

function apply(): void {
  const resolved = resolve();
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
}

function emit(): void {
  apply();
  for (const listener of listeners) listener();
}

// Follow the OS setting while the choice is 'system'.
try {
  window.matchMedia(DARK_QUERY).addEventListener('change', () => {
    if (theme === 'system') emit();
  });
} catch {
  // matchMedia is missing in some embedded contexts; the stored choice still works.
}

// Another tab changed the choice.
try {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEYS.theme) return;
    theme = isTheme(event.newValue) ? event.newValue : 'system';
    emit();
  });
} catch {
  // No storage events in a blocked frame.
}

function getTheme(): Theme {
  return theme;
}

export function getResolvedTheme(): ResolvedTheme {
  return resolve();
}

function setTheme(next: Theme): void {
  theme = next;
  writeStorage(STORAGE_KEYS.theme, next);
  emit();
}

/** Flips to the opposite of the theme in effect (palette command "Toggle theme"). */
export function toggleTheme(): Theme {
  const next = resolve() === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The stored choice and the theme in effect, kept current. */
export function useTheme(): {
  theme: Theme;
  resolved: ResolvedTheme;
  setTheme: (t: Theme) => void;
} {
  const current = useSyncExternalStore(subscribe, getTheme, getTheme);
  const resolved = useSyncExternalStore(
    subscribe,
    getResolvedTheme,
    getResolvedTheme,
  );
  return { theme: current, resolved, setTheme };
}
